import { type ReactNode } from 'react';
import { ThemeProvider, createTheme, type Theme } from '@mui/material/styles';
import { createAppTheme } from '@config/theme/theme';
import { useTenantConfigContext } from '@providers/TenantConfigProvider';
import { useDynamicFonts } from '@hooks/useDynamicFonts';
import { useTenantFavicon } from '@hooks/useTenantFavicon';

// Shadow-DOM variant: portals disabled so all MUI overlays render inside the
// shadow root rather than escaping to document.body. Mirrors the static variant
// in ThemeWrapper, but applied to the tenant-themed base so it survives the
// dynamic re-theme (an object theme fully replaces the outer one).
const withDisabledPortals = (base: Theme): Theme =>
  createTheme(base, {
    components: {
      MuiDialog: { defaultProps: { disablePortal: true } },
      MuiModal: { defaultProps: { disablePortal: true } },
      MuiPopover: { defaultProps: { disablePortal: true } },
      MuiPopper: { defaultProps: { disablePortal: true } },
      MuiTooltip: { defaultProps: { disablePortal: true } },
    },
  });

type DynamicMuiThemeProviderProps = {
  /** When embedded as a web component, disable MUI portals (shadow-DOM boundary). */
  isShadowDom?: boolean;
  children: ReactNode;
};

/**
 * Applies the tenant's DB-driven theme. Rendered below TenantConfigProvider so it
 * can read the resolved overrides, and overrides the static default theme set by
 * ThemeWrapper for the whole application subtree.
 */
export const DynamicMuiThemeProvider = ({
  isShadowDom = false,
  children,
}: DynamicMuiThemeProviderProps): JSX.Element => {
  const { themeOverrides, branding } = useTenantConfigContext();
  useDynamicFonts(themeOverrides);
  // Skipped under shadow DOM: an embedded web component must not re-icon its host.
  useTenantFavicon(branding?.faviconUrl, !isShadowDom);
  const base = createAppTheme(themeOverrides);
  const theme = isShadowDom ? withDisabledPortals(base) : base;

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
